/**
 * ObstacleManager.js
 */

import { COLORS, LINES, OBSTACLE } from "../Global/materials";
import * as THREE from "three";
import { ObstacleTypes, POLYGONS, RENDER_LAYERS, VARS } from "../Global/variables";
import { addExtrude, updateObstacleShape } from "./Modifications";
import { addOffset, createCirGeo, createRecGeo, projectObstaclePointsToRoof } from "./Shapes";
import { createSegmentPlane } from "../Global/functions";
import { sceneManager } from "../SetUps/SceneManager";
import { pushUndoAction } from "../Global/UndoManager";
import { getVertices, polygonsOverlapOnPlane } from "../Panels/Helpers";
import { refreshPanelOverlapHighlights } from "../Panels/ValidationVisuals";
import { notifyIrradianceNeedsRecompute } from "../Shading/IrradianceEvents";

const obstacleSnapRaycaster = new THREE.Raycaster();
const DEFAULT_OBSTACLE_HEIGHT = 0.2;
const POLYGON_CLOSE_THRESHOLD = 0.2;

function normalizeObstaclePoints(points) {
  if (!Array.isArray(points)) return [];
  return points.map((p) => {
    if (p?.isVector3) return p;
    if (p && typeof p.x === "number" && typeof p.y === "number") {
      const z = typeof p.z === "number" ? p.z : 0;
      return new THREE.Vector3(p.x, p.y, z);
    }
    return new THREE.Vector3();
  });
}

function snapPointToModelSurface(point, plane, meshes) {
  if (!meshes || !meshes.length || !plane) return point.clone();

  const normal = plane.normal.clone().normalize();
  const dirDown = normal.clone().negate();
  const dirUp = normal.clone();

  const origin1 = point.clone().add(normal.clone().multiplyScalar(2));
  obstacleSnapRaycaster.set(origin1, dirDown);
  let hits = obstacleSnapRaycaster.intersectObjects(meshes, true);
  if (hits.length) return hits[0].point.clone();

  const origin2 = point.clone().add(normal.clone().negate().multiplyScalar(2));
  obstacleSnapRaycaster.set(origin2, dirUp);
  hits = obstacleSnapRaycaster.intersectObjects(meshes, true);
  if (hits.length) return hits[0].point.clone();

  return point.clone();
}


function findTopSegmentUnderRay(ray) {
  const origin = ray.origin.clone();
  let best = null;

  for (let idx = 0; idx < POLYGONS.length; idx++) {
    const plane = createSegmentPlane(idx);
    if (!plane) continue;

    const hit = new THREE.Vector3();
    if (!ray.intersectPlane(plane, hit)) continue;

    const poly = getSegmentPolygon2D(idx);
    if (!poly) continue;

    const p2d = new THREE.Vector2(hit.x, hit.y);
    if (!pointInPolygon2D(p2d, poly)) continue;

    const dist = origin.distanceTo(hit);
    if (!best || dist < best.distance) {
      best = { segmentIndex: idx, hitPoint: hit.clone(), distance: dist };
    }
  }

  return best; // or null
}

function isFirstClickAllowedByRay(ray, parentSegmentIndex) {
  const hitSeg = findTopSegmentUnderRay(ray);

  // outside all segments ✅
  if (!hitSeg) return true;

  // only allow if the topmost segment under cursor is the parent ✅
  return hitSeg.segmentIndex === parentSegmentIndex;
}

function snapPointsToModelSurface(points, segmentIndex) {
  const meshes = VARS.Meshes || [];
  if (!meshes.length || !points || !points.length) return points;

  const plane = createSegmentPlane(segmentIndex);
  if (!plane) return points;

  return points.map((p) => snapPointToModelSurface(p, plane, meshes));
}

function getSegmentPolygon2D(segmentIndex) {
  const segment = POLYGONS[segmentIndex];
  if (!segment || !segment.edges || !segment.edges.length) return null;

  const segPoints2D = [];
  segment.edges.forEach((edge) => {
    if (edge.point1) segPoints2D.push(new THREE.Vector2(edge.point1.x, edge.point1.y));
    if (edge.point2) segPoints2D.push(new THREE.Vector2(edge.point2.x, edge.point2.y));
  });

  const uniquePoints = [];
  for (let p of segPoints2D) {
    if (!uniquePoints.some((u) => u.distanceTo(p) < 1e-6)) uniquePoints.push(p);
  }
  if (!uniquePoints.length) return null;

  const center = uniquePoints
    .reduce((acc, p) => acc.add(p), new THREE.Vector2())
    .divideScalar(uniquePoints.length);

  uniquePoints.sort(
    (a, b) =>
      Math.atan2(a.y - center.y, a.x - center.x) - Math.atan2(b.y - center.y, b.x - center.x)
  );

  return uniquePoints;
}


function debugRayHitObstaclePlacement(ray, parentSegmentIndex) {
  // Try to find a segment under the ray (closest hit)
  const hitSeg = findTopSegmentUnderRay(ray);

  // If you also want to log if the ray hits ANYTHING else (non-segment scene objects),
  // you can optionally add "other hit" info here (see section 3).
  const hitInfo = {
    hitType: hitSeg ? "segment" : "none",
    segmentIndex: hitSeg ? hitSeg.segmentIndex : null,
    worldPoint: hitSeg ? hitSeg.hitPoint : null,
    isParentSegment: hitSeg ? hitSeg.segmentIndex === parentSegmentIndex : false,
  };

  return hitInfo;
}

function pointInPolygon2D(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * ✅ NEW: Return all segments (indices) whose 2D polygon contains the worldPoint (XY)
 */
function getContainingSegments(worldPoint) {
  const p2d = new THREE.Vector2(worldPoint.x, worldPoint.y);
  const hits = [];

  for (let i = 0; i < POLYGONS.length; i++) {
    const poly = getSegmentPolygon2D(i);
    if (!poly) continue;
    if (pointInPolygon2D(p2d, poly)) hits.push(i);
  }
  return hits;
}

/**
 * ✅ NEW RULE:
 * First click is allowed if it's:
 *  - inside the parent segment, OR
 *  - outside all segments
 * but NOT inside any other segment besides the parent.
 */

function setCursor(cursor) {
  const dom = sceneManager?.renderer?.domElement;
  if (dom) dom.style.cursor = cursor;
}

class ObstacleManager {
  constructor(
    scene,
    camera,
    obstacleType,
    stopDrawing,
    updateObstacleList,
    selectedIndices,
    obsIndex = null,
    setPolygons = null
  ) {
    this.scene = scene;
    this.camera = camera;
    this.startPoint = null;
    this.endPoint = null;
    this.plane = new THREE.Plane();
    this.box = OBSTACLE.box;
    this.circle = OBSTACLE.circle;
    this.type = obstacleType;
    this.isDrawingStarted = false;
    this.rayCaster = new THREE.Raycaster();
    this.stopDrawing = stopDrawing;
    this.updateObstacleList = updateObstacleList;
    this.obsIndex = obsIndex;
    this.setPolygons = setPolygons;
    this.points = [];
    this.currentHoverPoint = null;
    this._lastHoverAllowed = null;
this._lastHoverHitKey = null;
    this.box.renderOrder = RENDER_LAYERS.OBSTACLE;
    this.circle.renderOrder = RENDER_LAYERS.OBSTACLE;


    if (typeof selectedIndices === "object" && selectedIndices !== null) {
      this.selectedSegment = selectedIndices.segmentIndex;
    } else {
      this.selectedSegment = selectedIndices;
    }
  }

  handleMouseDown(point) {
    if (this.selectedSegment == null) return;
    if (this.type === ObstacleTypes.POLYGON) {
      this.handlePolygonMouseDown(point);
      return;
    }

    if (!this.isDrawingStarted) {
      // Always draw on the parent plane
      this.plane = createSegmentPlane(this.selectedSegment);
      if (!this.plane) return;

      const hit = new THREE.Vector3();
      this.rayCaster.setFromCamera(point, this.camera);

      if (!this.rayCaster.ray.intersectPlane(this.plane, hit)) {
        setCursor("not-allowed");
        return;
      }

      // ✅ CHANGED: allow start anywhere EXCEPT inside other segments
      const allowed = isFirstClickAllowedByRay(this.rayCaster.ray, this.selectedSegment);
if (!allowed) {
  setCursor("not-allowed");
  return;
}


      this.startPoint = point;
      this.isDrawingStarted = true;
      setCursor("crosshair");
    } else {
      // ✅ Finalize anywhere (even completely outside parent) — no segment overlap restriction
      switch (this.type) {
        case ObstacleTypes.RECTANGLE:
          this.box.visible = false;
          this.completeShape(this.points, DEFAULT_OBSTACLE_HEIGHT, 6, COLORS.red, "rectangle");
          break;
        case ObstacleTypes.CIRCLE:
          this.circle.visible = false;
          this.completeShape(this.points, DEFAULT_OBSTACLE_HEIGHT, 6, COLORS.red, "circle");
          break;
      }

      this.isDrawingStarted = false;
      this.stopDrawing();
      setCursor("auto");
    }
  }

  getPlaneHitFromNdc(point) {
    const hit = new THREE.Vector3();
    this.rayCaster.setFromCamera(point, this.camera);
    return this.rayCaster.ray.intersectPlane(this.plane, hit) ? hit : null;
  }

  isPolygonCloseToFirst(point) {
    if (!point || !this.points.length) return false;
    return point.distanceTo(this.points[0]) <= POLYGON_CLOSE_THRESHOLD;
  }

  drawPolygonPreview(hoverPoint = null) {
    const previewPoints = this.points.map((p) => p.clone());
    if (hoverPoint) previewPoints.push(hoverPoint.clone());
    if (previewPoints.length < 2) return;

    const g = new THREE.BufferGeometry().setFromPoints(previewPoints);
    this.box.visible = true;
    this.box.geometry.dispose();
    this.box.geometry = g;
    this.box.material = LINES.redMaterial.clone();
    this.box.renderOrder = RENDER_LAYERS.OBSTACLE;
    this.box.geometry.attributes.position.needsUpdate = true;
  }

  handlePolygonMouseDown(point) {
    // First click starts polygon drawing and locks to parent segment plane.
    if (!this.isDrawingStarted) {
      this.plane = createSegmentPlane(this.selectedSegment);
      if (!this.plane) return;

      this.rayCaster.setFromCamera(point, this.camera);
      if (!isFirstClickAllowedByRay(this.rayCaster.ray, this.selectedSegment)) {
        setCursor("not-allowed");
        return;
      }

      const firstHit = this.getPlaneHitFromNdc(point);
      if (!firstHit) return;

      this.points = [firstHit.clone()];
      this.currentHoverPoint = firstHit.clone();
      this.isDrawingStarted = true;
      this.box.visible = true;
      setCursor("crosshair");
      return;
    }

    const hit = this.getPlaneHitFromNdc(point);
    if (!hit) return;

    if (this.points.length >= 3 && this.isPolygonCloseToFirst(hit)) {
      const closedPoints = [...this.points, this.points[0].clone()];
      this.box.visible = false;
      this.completeShape(
        closedPoints,
        DEFAULT_OBSTACLE_HEIGHT,
        6,
        COLORS.red,
        "polygon"
      );
      this.points = [];
      this.currentHoverPoint = null;
      this.isDrawingStarted = false;
      this.stopDrawing();
      setCursor("auto");
      return;
    }

    this.points.push(hit.clone());
    this.currentHoverPoint = hit.clone();
    this.drawPolygonPreview(this.currentHoverPoint);
  }

  handleMouseMove(point) {
  // ✅ Cursor hint ONLY for the first click eligibility
  if (!this.isDrawingStarted && this.selectedSegment != null) {
    const hit = new THREE.Vector3();
    this.rayCaster.setFromCamera(point, this.camera);

    // Use your ray-based segment hit
    const info = debugRayHitObstaclePlacement(this.rayCaster.ray, this.selectedSegment);

    // Determine "allowed" using your new rule
    const allowed = info.hitType === "none" || info.isParentSegment;

    // Build a stable key so we log only when something meaningful changes
    const hitKey = `${allowed}|${info.hitType}|${info.segmentIndex ?? "null"}|${
      info.worldPoint
        ? `${info.worldPoint.x.toFixed(3)},${info.worldPoint.y.toFixed(3)},${info.worldPoint.z.toFixed(3)}`
        : "null"
    }`;

    if (hitKey !== this._lastHoverHitKey) {
      this._lastHoverHitKey = hitKey;

      // console.log("🟦 Obstacle hover ray debug:", {
      //   allowed,
      //   parentSegment: this.selectedSegment,
      //   hitType: info.hitType,                 // "segment" | "none"
      //   hitSegment: info.segmentIndex,         // number | null
      //   isParentSegment: info.isParentSegment, // boolean
      //   worldPoint: info.worldPoint
      //     ? { x: info.worldPoint.x, y: info.worldPoint.y, z: info.worldPoint.z }
      //     : null,
      // });
    }

    setCursor(allowed ? "crosshair" : "not-allowed");
  }

  // existing drawing logic...
  if (this.isDrawingStarted) {
    this.endPoint = point;

      switch (this.type) {
      case ObstacleTypes.RECTANGLE:
        this.box.visible = true;
        this.drawRectangle();
        break;
      case ObstacleTypes.CIRCLE:
        this.circle.visible = true;
        this.drawCircle();
        break;
      case ObstacleTypes.POLYGON: {
        const hoverHit = this.getPlaneHitFromNdc(point);
        if (hoverHit) {
          this.currentHoverPoint = hoverHit.clone();
          this.drawPolygonPreview(this.currentHoverPoint);
        }
        this.box.visible = true;
        break;
      }
    }

    setCursor("crosshair");
  }
}


  drawRectangle() {
    let { points, g } = createRecGeo(
      this.startPoint,
      this.endPoint,
      this.plane,
      this.rayCaster,
      this.camera
    );

    const center = new THREE.Vector3();
    g.computeBoundingBox();
    g.boundingBox.getCenter(center);

    this.box.geometry.dispose();
    this.box.geometry = g;
    center.normalize();
    this.box.geometry.attributes.position.needsUpdate = true;
    this.box.renderOrder = RENDER_LAYERS.OBSTACLE;

    this.points = points;
  }

  drawCircle() {
    let { points, geo } = createCirGeo(
      this.startPoint,
      this.endPoint,
      this.plane,
      this.rayCaster,
      this.camera
    );

    const line = new THREE.Line(geo, LINES.redMaterial.clone());

    this.circle.geometry.dispose();
    this.circle.geometry = line.geometry;
    this.circle.material = line.material;
    this.circle.renderOrder = RENDER_LAYERS.OBSTACLE;

    this.points = points;
  }

  completeShape(points, height, offset, color, type, isFilled = false) {
    const inputPoints = normalizeObstaclePoints(points);
    const basePoints = projectObstaclePointsToRoof(inputPoints, this.selectedSegment);
    if (!basePoints.length) return;

    // ✅ DO NOT snap on creation. Snapping happens only on explicit flush (tick icon).
    const shapePoints = basePoints.map((p) => new THREE.Vector2(p.x, p.y));
    const shape = new THREE.Shape(shapePoints);

    let extrudeMesh = addExtrude(basePoints, shape, height, color, this.scene, isFilled);
    const ret = addOffset(extrudeMesh, basePoints, type, shape, offset, height, this.selectedSegment);

    const effectiveOffset = typeof ret.appliedOffset === "number" ? ret.appliedOffset : offset;
    this.addObstacle(type, ret.shape, ret.points, ret.mesh, height, effectiveOffset, color, isFilled);
  }

  addObstacle(type, shape, points, extrudeMesh, height, offset, color, isFilled = false) {
    let parentSegment = this.selectedSegment;

    if (this.obsIndex !== null) {
      const existing = POLYGONS[this.selectedSegment]?.obstructions?.[this.obsIndex];
      if (existing && typeof existing.parentSegment === "number") {
        parentSegment = existing.parentSegment;
      }
    }

    let obs = {
      type: type,
      polyShape: shape,
      cords: points,
      mesh: extrudeMesh,
      height: height,
      ogColor: color,
      offset: offset,
      isFilled: Boolean(isFilled),
      linkedSegments: [],
      parentSegment: parentSegment,
      isFlushed: false,
    };

    console.log("🟩 NEW OBSTACLE CREATED (NO FLUSH) →", {
      parentSegment,
      cords: points.map((p) => ({ x: p.x, y: p.y, z: p.z })),
    });

    if (this.obsIndex === null) {
      POLYGONS[this.selectedSegment].obstructions.push(obs);
      notifyIrradianceNeedsRecompute("obstacle-created");

      obs.linkedSegments = [parentSegment];
      refreshPanelOverlapHighlights();

      let uiData = { type: type, height: height, offset: offset, isFilled: Boolean(isFilled) };
      this.updateObstacleList({
        newObstacle: uiData,
        segmentIndex: this.selectedSegment,
      });

      const parentSegmentIndex = parentSegment;
      const obstacleRef = obs;
      const obstacleIndex = POLYGONS[this.selectedSegment].obstructions.length - 1;
      const linkedSnapshot = Array.isArray(obstacleRef.linkedSegments)
        ? obstacleRef.linkedSegments.slice()
        : [parentSegmentIndex];
      const isFlushedSnapshot = obstacleRef.isFlushed === true;
      const setPolygons = this.setPolygons;

      const removeObstacleFromScene = () => {
        if (!obstacleRef || !obstacleRef.mesh) return;
        const mesh = obstacleRef.mesh;
        if (mesh.parent) {
          mesh.parent.remove(mesh);
        } else if (sceneManager.scene) {
          sceneManager.scene.remove(mesh);
        }
      };

      const removeObstacleFromSegments = () => {
        POLYGONS.forEach((seg) => {
          if (!seg || !Array.isArray(seg.obstructions)) return;
          seg.obstructions = seg.obstructions.filter((o) => o !== obstacleRef);
        });
      };

      const addObstacleToSegments = () => {
        const targets = linkedSnapshot.length ? linkedSnapshot : [parentSegmentIndex];
        targets.forEach((idx) => {
          const seg = POLYGONS[idx];
          if (!seg || !Array.isArray(seg.obstructions)) return;
          if (seg.obstructions.includes(obstacleRef)) return;
          if (idx === parentSegmentIndex) {
            const insertIndex = Math.min(obstacleIndex, seg.obstructions.length);
            seg.obstructions.splice(insertIndex, 0, obstacleRef);
          } else {
            seg.obstructions.push(obstacleRef);
          }
        });
        obstacleRef.parentSegment = parentSegmentIndex;
        obstacleRef.linkedSegments = targets.slice();
        obstacleRef.isFlushed = isFlushedSnapshot;
      };

      const removeObstacleFromUI = () => {
        if (typeof setPolygons !== "function") return;
        setPolygons((prev) => {
          if (!prev || !prev[parentSegmentIndex]) return prev;
          const next = [...prev];
          const seg = { ...next[parentSegmentIndex] };
          const obsList = Array.isArray(seg.obstructions) ? [...seg.obstructions] : [];
          if (obstacleIndex < 0 || obstacleIndex >= obsList.length) return prev;
          obsList.splice(obstacleIndex, 1);
          seg.obstructions = obsList;
          next[parentSegmentIndex] = seg;
          return next;
        });
      };

      const addObstacleToUI = () => {
        if (typeof setPolygons !== "function") return;
        setPolygons((prev) => {
          if (!prev || !prev[parentSegmentIndex]) return prev;
          const next = [...prev];
          const seg = { ...next[parentSegmentIndex] };
          const obsList = Array.isArray(seg.obstructions) ? [...seg.obstructions] : [];
          const insertIndex = Math.min(obstacleIndex, obsList.length);
          obsList.splice(insertIndex, 0, uiData);
          seg.obstructions = obsList;
          next[parentSegmentIndex] = seg;
          return next;
        });
      };

      pushUndoAction({
        label: "Add obstacle",
        undo: () => {
          removeObstacleFromScene();
          removeObstacleFromSegments();
          removeObstacleFromUI();
          obstacleRef.linkedSegments = [];
          notifyIrradianceNeedsRecompute("obstacle-removed");
          refreshPanelOverlapHighlights();
        },
        redo: () => {
          addObstacleToSegments();
          addObstacleToUI();
          if (obstacleRef && obstacleRef.mesh && !obstacleRef.mesh.parent) {
            sceneManager.scene.add(obstacleRef.mesh);
          }
          notifyIrradianceNeedsRecompute("obstacle-created");
          refreshPanelOverlapHighlights();
        },
      });
    } else {
      const old = POLYGONS[this.selectedSegment]?.obstructions?.[this.obsIndex];
      if (old && typeof old.isFlushed === "boolean") {
        obs.isFlushed = old.isFlushed;
      }
      if (old && typeof old.isFilled === "boolean") {
        obs.isFilled = old.isFilled;
      }
      if (old && Array.isArray(old.linkedSegments)) {
        obs.linkedSegments = old.linkedSegments.slice();
      } else {
        obs.linkedSegments = [parentSegment];
      }
      POLYGONS[this.selectedSegment].obstructions.splice(this.obsIndex, 1, obs);
    }
  }
}

/**
 * Determines which roof segments an obstacle overlaps based on edge boundaries.
 */
function getIntersectingSegmentsFromEdges(obstaclePoints) {
  const overlappingSegments = [];
  const obstacle2D = obstaclePoints.map((p) => new THREE.Vector2(p.x, p.y));

  const obsMinX = Math.min(...obstacle2D.map((p) => p.x));
  const obsMaxX = Math.max(...obstacle2D.map((p) => p.x));
  const obsMinY = Math.min(...obstacle2D.map((p) => p.y));
  const obsMaxY = Math.max(...obstacle2D.map((p) => p.y));

  POLYGONS.forEach((segment, idx) => {
    if (!segment || !segment.edges || segment.edges.length === 0) return;

    const segPoints2D = [];
    segment.edges.forEach((edge) => {
      if (edge.point1) segPoints2D.push(new THREE.Vector2(edge.point1.x, edge.point1.y));
      if (edge.point2) segPoints2D.push(new THREE.Vector2(edge.point2.x, edge.point2.y));
    });

    const uniquePoints = [];
    for (let p of segPoints2D) {
      if (!uniquePoints.some((u) => u.distanceTo(p) < 1e-6)) uniquePoints.push(p);
    }
    if (!uniquePoints.length) return;

    const center = uniquePoints
      .reduce((acc, p) => acc.add(p), new THREE.Vector2())
      .divideScalar(uniquePoints.length);
    uniquePoints.sort(
      (a, b) =>
        Math.atan2(a.y - center.y, a.x - center.x) - Math.atan2(b.y - center.y, b.x - center.x)
    );

    const segMinX = Math.min(...uniquePoints.map((p) => p.x));
    const segMaxX = Math.max(...uniquePoints.map((p) => p.x));
    const segMinY = Math.min(...uniquePoints.map((p) => p.y));
    const segMaxY = Math.max(...uniquePoints.map((p) => p.y));

    const bboxIntersects =
      obsMinX <= segMaxX && obsMaxX >= segMinX && obsMinY <= segMaxY && obsMaxY >= segMinY;
    if (!bboxIntersects) return;

    if (polygonOverlap2D(uniquePoints, obstacle2D)) {
      overlappingSegments.push(idx);
    }
  });

  return overlappingSegments;
}

function polygonOverlap2D(polyA, polyB) {
  return polyA.some((p) => pointInPolygon2D(p, polyB)) || polyB.some((p) => pointInPolygon2D(p, polyA));
}

/**
 * ✅ Flush by index (reliable).
 * - snaps to surface (ONLY HERE)
 * - rebuilds geometry once
 * - then attaches to intersecting segments
 */
export function flushObstacleToSceneByIndex(segmentIndex, obstacleIndex) {
  const obstruction = POLYGONS?.[segmentIndex]?.obstructions?.[obstacleIndex];
  if (!obstruction) return;

  if (obstruction.isFlushed) return;

  const parent = obstruction.parentSegment;
  if (typeof parent !== "number") return;

  // ✅ Snap only now (explicit flush)
  const snapped = snapPointsToModelSurface(obstruction.cords, parent);

  // IMPORTANT: update by the index inside the PARENT list (owner list)
  const parentIdx = POLYGONS?.[parent]?.obstructions?.indexOf(obstruction);
  if (parentIdx === -1) return;

  // Rebuild mesh/offset to match snapped points
  updateObstacleShape(parent, parentIdx, snapped);

  // Attach obstruction to every segment overlapped by its current boundary/offset.
  const boundaryPoints = getObstacleBoundaryPointsFromMesh(obstruction.mesh, obstruction.cords);
  const targetsRaw = getIntersectingSegmentsFromEdges(boundaryPoints);
  const targets = Array.from(new Set([parent, ...targetsRaw]));

  const previousLinked = Array.isArray(obstruction.linkedSegments)
    ? obstruction.linkedSegments.slice()
    : [];

  POLYGONS.forEach((seg, idx) => {
    if (!seg || !Array.isArray(seg.obstructions)) return;
    if (targets.includes(idx)) {
      if (!seg.obstructions.includes(obstruction)) seg.obstructions.push(obstruction);
    } else {
      seg.obstructions = seg.obstructions.filter((o) => o !== obstruction);
    }
  });

  obstruction.linkedSegments = targets;
  obstruction.isFlushed = true;
  refreshPanelOverlapHighlights(Array.from(new Set([...previousLinked, ...targets])));

  console.log("✅ Flushed obstacle to scene. linkedSegments:", obstruction.linkedSegments);
}

function getObstacleBoundaryPointsFromMesh(mesh, fallbackPoints) {
  if (mesh?.updateMatrixWorld) mesh.updateMatrixWorld(true);
  const storedOffsetOutline = mesh?.userData?.obstacleOffsetOutlineWorld;
  if (Array.isArray(storedOffsetOutline) && storedOffsetOutline.length >= 3) {
    return storedOffsetOutline.map((p) => p.clone());
  }
  const offset = mesh?.getObjectByName?.("offset");
  if (offset?.geometry?.attributes?.position) {
    if (offset.updateMatrixWorld) offset.updateMatrixWorld(true);
    return getVertices(offset);
  }
  if (Array.isArray(fallbackPoints)) return fallbackPoints;
  return [];
}

function obstacleOverlapsPanelsInSegment(mesh, fallbackPoints, segmentIndex) {
  const obstaclePoints = getObstacleBoundaryPointsFromMesh(mesh, fallbackPoints);
  if (!obstaclePoints.length) return false;

  const basePlane = typeof segmentIndex === "number" ? createSegmentPlane(segmentIndex) : null;
  const baseNormal = basePlane?.normal?.clone().normalize() || null;
  const planeTolerance = 0.05; // 5 cm

  const candidateSegments = [];
  for (let i = 0; i < POLYGONS.length; i++) {
    const seg = POLYGONS[i];
    if (!seg || !Array.isArray(seg.panels) || seg.panels.length === 0) continue;

    const plane = createSegmentPlane(i);
    if (!plane) continue;

    if (baseNormal) {
      const dot = Math.abs(baseNormal.dot(plane.normal.clone().normalize()));
      if (dot < 0.99) continue;
    }

    const dist = Math.abs(plane.distanceToPoint(obstaclePoints[0]));
    if (dist > planeTolerance) continue;

    candidateSegments.push({ index: i, plane });
  }

  if (!candidateSegments.length && typeof segmentIndex === "number") {
    const plane = createSegmentPlane(segmentIndex);
    if (plane) {
      candidateSegments.push({ index: segmentIndex, plane });
    }
  }

  for (const { index, plane } of candidateSegments) {
    const segment = POLYGONS[index];
    if (!segment || !Array.isArray(segment.panels) || segment.panels.length === 0) continue;
    const normal = plane.normal.clone().normalize();

    for (const panelData of segment.panels) {
      const panel = panelData?.panel;
      if (!panel) continue;
      if (panel.updateMatrixWorld) panel.updateMatrixWorld(true);
      const panelPoints = getVertices(panel);
      if (!panelPoints.length) continue;
      if (polygonsOverlapOnPlane(panelPoints, obstaclePoints, normal, panel.position)) {
        return true;
      }
    }
  }

  return false;
}

function disposeObstacleMesh(mesh) {
  if (!mesh) return;
  if (mesh.parent) {
    mesh.parent.remove(mesh);
  } else if (sceneManager.scene) {
    sceneManager.scene.remove(mesh);
  }
  mesh.traverse?.((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) obj.material.dispose();
  });
}

/**
 * Backward-compatible
 */
export function flushObstacleToScene(obstruction) {
  if (!obstruction) return;
  const parent = obstruction.parentSegment;
  if (typeof parent !== "number") return;

  const idx = POLYGONS?.[parent]?.obstructions?.indexOf(obstruction);
  if (idx === -1) return;

  flushObstacleToSceneByIndex(parent, idx);
}

export function updateLinkedSegmentsAfterMove(obstruction) {
  if (!obstruction || !obstruction.cords) return;

  const parent = obstruction.parentSegment;
  if (typeof parent !== "number") return;

  const boundaryPoints = getObstacleBoundaryPointsFromMesh(obstruction.mesh, obstruction.cords);
  const targetsRaw = getIntersectingSegmentsFromEdges(boundaryPoints);
  const targets = Array.from(new Set([parent, ...targetsRaw]));

  const previousLinked = Array.isArray(obstruction.linkedSegments)
    ? obstruction.linkedSegments.slice()
    : [];

  POLYGONS.forEach((seg, idx) => {
    if (!seg || !Array.isArray(seg.obstructions)) return;
    if (targets.includes(idx)) {
      if (!seg.obstructions.includes(obstruction)) seg.obstructions.push(obstruction);
    } else {
      seg.obstructions = seg.obstructions.filter((o) => o !== obstruction);
    }
  });

  obstruction.linkedSegments = targets;
  refreshPanelOverlapHighlights(Array.from(new Set([...previousLinked, ...targets])));
}

export default ObstacleManager;
